import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useState } from 'react';
import { toast } from 'sonner';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { P, usePermissions } from '@/features/permissions';
import { type EncounterOut, useDeleteEncounter } from '../api/encounters-api';
import { formatCalendarDate } from '@/lib/utils/date';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';

export function DeleteEncounterButton({
  encounter,
  onDeleted,
  disabled = false,
}: {
  encounter: EncounterOut;
  onDeleted: () => void;
  disabled?: boolean;
}) {
  const { can } = usePermissions();
  const remove = useDeleteEncounter();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  if (!can(P.DELETE_ENCOUNTER) || !['draft', 'complete', 'submitted'].includes(encounter.status)) return null;
  async function confirm() {
    if (remove.isPending || disabled) return;
    setError('');
    try {
      await remove.mutateAsync(encounter.id);
      setOpen(false);
      toast.success('Encounter and associated reports deleted');
      onDeleted();
    } catch (error) {
      const response = (error as { response?: { status?: number; data?: { detail?: unknown } } }).response;
      if (response?.status === 409 && typeof response.data?.detail === 'string') {
        setError(response.data.detail);
        return;
      }
      if (response?.status === 403) {
        setError('You do not have permission to delete this encounter.');
        return;
      }
      setError(
        'Could not delete this encounter. Encounters with finalized reports or clinical flowsheet entries cannot be deleted. Refresh the chart and try again if it is still unfinished.',
      );
    }
  }
  return (
    <div onClick={(event) => event.stopPropagation()}>
      <AlertDialog
        open={open}
        onOpenChange={(value) => {
          if (!remove.isPending) setOpen(value);
        }}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="icon" className="min-h-11 min-w-11" aria-label="Encounter actions" disabled={disabled || remove.isPending}>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem variant="destructive" onSelect={() => { setError(''); setOpen(true); }}>
                <Trash2 />Delete encounter
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete encounter from {formatCalendarDate(encounter.encounter_date)}?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the encounter and its associated draft or failed reports from the chart and Inbox. You will
              return to Encounters after deletion. Active processing will be asked to stop; retry deletion after it stops. Signed or finalized reports, clinical flowsheet
              entries, and reports shared with another encounter prevent deletion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && <p role="alert">{error}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <Button type="button" variant="destructive" disabled={remove.isPending} onClick={() => void confirm()}>
              {remove.isPending ? 'Deleting…' : 'Delete encounter'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
